/**
 * AcademyPage - Bible Academy main page with course list and progress.
 */

import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Layout } from "../../../components/layout/index.ts";
import { getTelegram } from "../../../lib/telegram.ts";
import { ListSkeleton } from "../../../components/ui/Skeleton.tsx";
import { IconAcademicCap, IconCheck, IconPlay, IconExternalLink } from "../../../components/Icons.tsx";
import { isTelegramMiniApp } from "../../../lib/telegram.ts";

const BIBLE_ACADEMY_URL = "https://www.bible-life.academy/";
import { ErrorDisplay } from "../../../components/ErrorDisplay.tsx";
import { useCourses } from "../hooks";
import type { AcademyCourse } from "../hooks/useCourses";

// =============================================================================
// HELPERS
// =============================================================================

type CourseStatus = "passed" | "in_progress" | "not_started";

function getCourseStatus(course: AcademyCourse): CourseStatus {
  const progress = course.userProgress;
  if (!progress) return "not_started";
  if (progress.examPassed) return "passed";
  if (progress.examStarted || progress.visited) return "in_progress";
  return "not_started";
}

function getStatusLabel(status: CourseStatus): string {
  switch (status) {
    case "passed":
      return "Пройден";
    case "in_progress":
      return "В процессе";
    case "not_started":
      return "Начать";
  }
}

function getStatusColor(status: CourseStatus): string {
  switch (status) {
    case "passed":
      return "text-emerald-600 bg-emerald-50";
    case "in_progress":
      return "text-amber-600 bg-amber-50";
    case "not_started":
      return "text-blue-600 bg-blue-50";
  }
}

// =============================================================================
// COURSE CARD COMPONENT
// =============================================================================

interface CourseCardProps {
  course: AcademyCourse;
}

function CourseCard({ course }: CourseCardProps) {
  const status = getCourseStatus(course);
  const statusLabel = getStatusLabel(status);
  const statusColor = getStatusColor(status);
  const questionsCount = course.exam?.questionsCount ?? 0;

  return (
    <Link
      to={`/academy/${course.id}`}
      className="block rounded-xl lg-mobile:rounded-2xl overflow-hidden bg-white/95 border shadow-sm transition-all active:scale-[0.99] border-slate-100"
    >
      <div className="flex">
        {/* Left accent stripe */}
        <div
          className={`flex-shrink-0 w-1.5 rounded-l-xl lg-mobile:rounded-l-2xl ${
            status === "passed"
              ? "bg-emerald-400/60"
              : status === "in_progress"
                ? "bg-amber-400/60"
                : "bg-blue-400/60"
          }`}
          aria-hidden
        />

        {/* Icon section */}
        <div className="relative flex-shrink-0 w-16 xs:w-20 lg-mobile:w-24 flex flex-col items-center justify-center gap-1 py-3 xs:py-4 lg-mobile:py-5 border-r border-slate-100 overflow-hidden bg-blue-50/30">
          {status === "passed" ? (
            <IconCheck className="w-5 h-5 xs:w-6 xs:h-6 lg-mobile:w-7 lg-mobile:h-7 text-emerald-600" />
          ) : (
            <IconAcademicCap className="w-5 h-5 xs:w-6 xs:h-6 lg-mobile:w-7 lg-mobile:h-7 text-blue-600/75" />
          )}
          {questionsCount > 0 && (
            <span className="text-xs xs:text-sm font-medium text-slate-500">
              {questionsCount} воп.
            </span>
          )}
        </div>

        {/* Content section */}
        <div className="flex-1 min-w-0 p-3 xs:p-4 lg-mobile:p-5">
          <div className="flex items-start gap-2 mb-1 lg-mobile:mb-2">
            <h3 className="text-sm xs:text-base lg-mobile:text-lg font-semibold text-slate-900 line-clamp-2 flex-1 min-w-0">
              {course.title}
            </h3>
            <span
              className={`flex-shrink-0 px-1.5 py-0.5 text-[9px] xs:text-[10px] font-medium rounded ${statusColor}`}
            >
              {statusLabel}
            </span>
          </div>

          {course.description && (
            <p className="text-xs xs:text-sm lg-mobile:text-base text-slate-500 line-clamp-2 mb-2 lg-mobile:mb-3">
              {course.description}
            </p>
          )}

          <div className="flex items-center justify-between">
            <span className="text-xs xs:text-sm text-slate-400">
              {course.exam ? "Контрольные вопросы" : "Курс"}
            </span>
            <span className="flex items-center gap-1 text-xs xs:text-sm text-blue-600">
              {status === "not_started" ? (
                <>
                  <IconPlay className="w-3 h-3" />
                  Начать
                </>
              ) : (
                "Подробнее →"
              )}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

// =============================================================================
// MAIN PAGE COMPONENT
// =============================================================================

export function AcademyPage() {
  const navigate = useNavigate();
  const tg = getTelegram();
  const { courses, isLoading, error, refetch } = useCourses();

  // BackButton (Mini App / PWA)
  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/");
      }
    };
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate]);

  if (error) {
    return (
      <Layout>
        <ErrorDisplay
          error={error.message}
          onRetry={refetch}
          className="mt-6 border border-slate-100 ring-1 ring-slate-200/50"
        />
      </Layout>
    );
  }

  // Sort: passed last, then by sortOrder
  const sortedCourses = [...courses].sort((a, b) => {
    const aStatus = getCourseStatus(a);
    const bStatus = getCourseStatus(b);

    // Passed courses go last
    if (aStatus === "passed" && bStatus !== "passed") return 1;
    if (aStatus !== "passed" && bStatus === "passed") return -1;

    // In progress before not started
    if (aStatus === "in_progress" && bStatus === "not_started") return -1;
    if (aStatus === "not_started" && bStatus === "in_progress") return 1;

    // Otherwise by sortOrder
    return a.sortOrder - b.sortOrder;
  });

  return (
    <Layout>
      {/* Hero section */}
      <div className="rounded-2xl overflow-hidden bg-white/95 shadow-sm border border-slate-100 ring-1 ring-slate-200/50">
        <div className="p-6 xs:p-8">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-blue-50/80 flex items-center justify-center text-blue-600/75 flex-shrink-0">
              <IconAcademicCap className="w-6 h-6" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl xs:text-2xl font-bold text-slate-900">
                Библейская академия
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                Изучайте Библию и проверяйте знания с помощью контрольных вопросов
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* External academy link */}
      <div className="mt-4">
        <button
          type="button"
          onClick={() => {
            const tg = getTelegram();
            if (isTelegramMiniApp() && tg?.openLink) {
              tg.openLink(BIBLE_ACADEMY_URL);
            } else {
              window.open(BIBLE_ACADEMY_URL, "_blank", "noopener,noreferrer");
            }
          }}
          className="w-full flex items-center gap-3 p-4 rounded-xl bg-white/95 shadow-sm border border-slate-100 ring-1 ring-slate-200/50 text-left transition-all active:scale-[0.99]"
        >
          <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600 flex-shrink-0">
            <IconAcademicCap className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900">
              Перейти на сайт академии
            </p>
            <p className="text-xs text-slate-500">
              bible-life.academy
            </p>
          </div>
          <IconExternalLink className="w-5 h-5 text-slate-400 flex-shrink-0" />
        </button>
      </div>

      {/* Courses section */}
      <section className="mt-6">
        <p className="section-title px-0">Курсы</p>
        {isLoading ? (
          <div className="rounded-2xl overflow-hidden bg-white/95 shadow-sm border border-slate-100 ring-1 ring-slate-200/50 p-6">
            <ListSkeleton count={3} />
          </div>
        ) : sortedCourses.length === 0 ? (
          <div className="rounded-2xl overflow-hidden bg-white/95 shadow-sm border border-slate-100 ring-1 ring-slate-200/50 p-8 text-center">
            <p className="text-slate-500 text-sm font-medium uppercase tracking-wider">
              Скоро
            </p>
            <p className="text-slate-600 mt-1">Курсы появятся здесь</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sortedCourses.map((course) => (
              <CourseCard key={course.id} course={course} />
            ))}
          </div>
        )}
      </section>

      <div className="h-4" />
    </Layout>
  );
}
